import { createMemo, For, Show } from "solid-js";
import { ClipboardList, Clock3, ShieldCheck, Users } from "@fleet/icons";
import { msg } from "@lingui/core/macro";

import { Card, CardContent, PageContainer, PageHeader, Skeleton } from "@fleet/ui";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { createPowerSyncQuery } from "@jornada/shared/solid";
import {
  baselineCatalog,
  classifyCategoryTotals,
  type EmployeeProfileRecord,
  getEmployeeProfilesQuery,
  getPendingProposedCountQuery,
  getTeamUsageByCategoryQuery,
  productivityRatio,
  type ProductivitySummary,
  queryKeys,
  totalSecs,
} from "@jornada/shared";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { formatDuration, toPercent } from "../lib/format";

type UsageRow = { category: string | null; total_secs: number };

/** Stacked productive / neutral / off-task snapshot — categories only, never content. */
function ProductivitySnapshot(props: { summary: ProductivitySummary }) {
  const { t } = useI18n();
  const total = () => totalSecs(props.summary);
  const pct = (secs: number) => (total() === 0 ? 0 : (secs / total()) * 100);
  const segments = () => [
    {
      key: "productive",
      label: t(msg`Productive`),
      secs: props.summary.productive_secs,
      cls: "bg-success",
    },
    { key: "neutral", label: t(msg`Neutral`), secs: props.summary.neutral_secs, cls: "bg-warning" },
    {
      key: "distracting",
      label: t(msg`Distracting`),
      secs: props.summary.distracting_secs,
      cls: "bg-muted-foreground/60",
    },
  ];
  return (
    <div class="space-y-3">
      <div class="flex h-3 w-full overflow-hidden rounded-full bg-muted ring-1 ring-inset ring-border/40">
        <For each={segments()}>
          {(s) => (
            <div
              class={`h-full ${s.cls} transition-[width] duration-500`}
              style={{ width: `${pct(s.secs)}%` }}
            />
          )}
        </For>
      </div>
      <div class="grid grid-cols-3 gap-2 text-center text-xs">
        <For each={segments()}>
          {(s) => (
            <div>
              <div class="font-bold text-foreground tabular-nums">{formatDuration(s.secs)}</div>
              <div class="mt-0.5 flex items-center justify-center gap-1.5 text-muted-foreground">
                <span class={`h-2 w-2 rounded-full ${s.cls}`} aria-hidden="true" />
                {s.label}
              </div>
            </div>
          )}
        </For>
      </div>
    </div>
  );
}

export function Overview() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const userId = () => session.data?.user.id;

  const profiles = createPowerSyncQuery<EmployeeProfileRecord>({
    queryKey: () => queryKeys.workforce.profiles(userId()),
    db: () => db(),
    queryFn: () => {
      const d = drizzle();
      return d ? getEmployeeProfilesQuery(d) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  const usage = createPowerSyncQuery<UsageRow>({
    queryKey: () => queryKeys.workforce.usageByCategory("__team__"),
    db: () => db(),
    queryFn: () => {
      const d = drizzle();
      return d ? getTeamUsageByCategoryQuery(d) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  const pending = createPowerSyncQuery<{ total: number }>({
    queryKey: () => queryKeys.workforce.pendingCount(userId()),
    db: () => db(),
    queryFn: () => {
      const d = drizzle();
      return d ? getPendingProposedCountQuery(d) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  const teamSummary = createMemo(() => classifyCategoryTotals(usage.data ?? [], baselineCatalog()));
  const teamRatioPct = createMemo(() => toPercent(productivityRatio(teamSummary())));
  const classifiedSecs = () => totalSecs(teamSummary());
  const pendingCount = () => pending.data?.[0]?.total ?? 0;
  const employeeCount = () => (profiles.data ?? []).length;

  return (
    <PageContainer>
      <PageHeader
        title="Jornada"
        description={t(msg`Manager dashboard — team activity with each employee's consent.`)}
      />

      {/* Humane transparency band (§2e): calm reassurance, not surveillance. */}
      <Card class="overflow-hidden border-accent/20 bg-accent/[0.04]">
        <CardContent class="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:gap-4">
          <div class="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent/12 text-accent ring-1 ring-inset ring-accent/20">
            <ShieldCheck class="h-6 w-6" />
          </div>
          <div class="min-w-0">
            <p class="text-sm font-semibold text-foreground">
              {t(msg`Only aggregated categories, never content.`)}
            </p>
            <p class="mt-0.5 text-sm text-muted-foreground">
              {t(
                msg`Every employee sees exactly what is collected. Nothing is recorded outside working hours, and no timesheet is billed without your review.`,
              )}
            </p>
          </div>
          <Link
            to="/transparency"
            class="shrink-0 self-start rounded-lg px-3 py-1.5 text-sm font-semibold text-accent ring-1 ring-inset ring-accent/25 transition-colors hover:bg-accent/10 sm:self-center"
          >
            {t(msg`View transparency`)}
          </Link>
        </CardContent>
      </Card>

      <Show when={!profiles.isLoading} fallback={<Skeleton class="mt-4 h-32 w-full" />}>
        <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Link to="/team" class="group block">
            <Card class="h-full transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:shadow-md">
              <CardContent class="flex items-center gap-4 pt-6">
                <div class="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent ring-1 ring-inset ring-accent/15">
                  <Users class="h-6 w-6" />
                </div>
                <div class="min-w-0">
                  <div
                    class="text-2xl font-black text-foreground tabular-nums"
                    data-testid="overview-team-ratio"
                  >
                    {teamRatioPct()}%
                  </div>
                  <div class="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t(msg`Team productivity`)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/review" class="group block">
            <Card class="h-full transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:shadow-md">
              <CardContent class="flex items-center gap-4 pt-6">
                <div class="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-warning/12 text-warning ring-1 ring-inset ring-warning/20">
                  <ClipboardList class="h-6 w-6" />
                </div>
                <div class="min-w-0">
                  <div
                    class="text-2xl font-black text-foreground tabular-nums"
                    data-testid="overview-pending-count"
                  >
                    {pendingCount()}
                  </div>
                  <div class="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t(msg`Timesheets to review`)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/transparency" class="group block">
            <Card class="h-full transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:shadow-md">
              <CardContent class="flex items-center gap-4 pt-6">
                <div class="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-success/12 text-success ring-1 ring-inset ring-success/20">
                  <ShieldCheck class="h-6 w-6" />
                </div>
                <div class="min-w-0">
                  <div class="text-2xl font-black text-foreground tabular-nums">
                    {employeeCount()}
                  </div>
                  <div class="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t(msg`Employees with consent`)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Team productivity snapshot — appears once there is classified activity. */}
        <Show when={classifiedSecs() > 0}>
          <Card class="mt-4">
            <CardContent class="space-y-4 pt-6">
              <div class="flex items-center justify-between gap-3">
                <div class="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Clock3 class="h-4 w-4 text-muted-foreground" />
                  {t(msg`Team activity today`)}
                </div>
                <span class="text-xs text-muted-foreground tabular-nums">
                  {formatDuration(classifiedSecs())} {t(msg`classified`)}
                </span>
              </div>
              <ProductivitySnapshot summary={teamSummary()} />
            </CardContent>
          </Card>
        </Show>
      </Show>
    </PageContainer>
  );
}
